import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfDropdownField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfInputDatepickerField,
  FrVrfMultipleLineInputField,
  FrVrfSingleLineDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import { useGetGeneralCodes } from "../../services/data-list";
import {
  UpdateTicketRequest,
  useGetInfraTaskTicket,
  useUpdateInfraTaskTicket,
} from "../../services/ticket";
import {
  StupTask,
  StupTasks,
  TcktDesc,
} from "../../services/ticket/get-infra-task-ticket/GetInfraTaskResponse";
import { UpdateInfraTaskTicketRequest } from "../../services/ticket/update-infra-task-ticket/UpdateInfraTaskRequest";
import { mappingErrorResponseToSpreadsheet } from "../../util";
import { removeMilliseconds } from "../../util/date-util";
import { useInfraTicketDescriptionDialogState } from "../dialog/infra-ticket-description-dialog";
import { usePageNavigation } from "../use-page-navigation";
import {
  REQUEST_CD_TYPES,
  TEMPLATE_FILE_URL,
  createTcktStsTypeList,
  getNecFlgCells,
  nextInfraEventCode,
  requestCdTypes,
} from "./TicketDetailPageConstant";
import { ticketDetailPageInitialState } from "./TicketDetailPageInitialState";
import {
  DescriptionHistorySpreadsheetState,
  InfraTaskSpreadsheetRecordData,
  InfraTaskSpreadsheetState,
  NecFlgCell,
} from "./TicketDetailPageState";
import { usePageState } from "./usePageState";

export const TicketDetailPageContent = () => {
  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const [_dialogState, setDialogState] = useInfraTicketDescriptionDialogState();

  const { searchResult, tcktStsList, dataList, fieldPath } = pageState;
  const { messageForScreen } = useMessage();
  const navigate = useNavigate();

  const { showConfirmationDialog, errorMessages, showToast, resetUserError } =
    useGlobalNotifier();
  const { isModalPage } = usePageNavigation();

  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    tcktId: queryParameter.get("tcktId") ?? "",
  };

  const [getInfraTaskTicketResponse, sendGetInfraTaskTicketRequest] =
    useGetInfraTaskTicket();
  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getUpdateTicketResponse, sendUpdateTicketRequest] =
    useUpdateInfraTaskTicket();

  const onGetGeneralCodesResponse = () => {
    if (!getGeneralCodesResponse) return;
    const tcktStsTypeCpntStgStupTask =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.TCKT_STS_TYPE_CPNT_STG_STUP_TASK,
      )?.cdList ?? [];
    const tcktStsTypeCpntPrdStupTask =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.TCKT_STS_TYPE_CPNT_PRD_STUP_TASK,
      )?.cdList ?? [];
    const tcktStsTypeCpntOtherStgStupTask =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) =>
          e.cdType === REQUEST_CD_TYPES.TCKT_STS_TYPE_CPNT_OTHER_STG_STUP_TASK,
      )?.cdList ?? [];
    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.IN_DEF_COMMON_FLAG_TYPE,
      )?.cdList ?? [];

    const roleType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.ROLE_TYPE,
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        tcktStsTypeCpntStgStupTask,
        tcktStsTypeCpntPrdStupTask,
        tcktStsTypeCpntOtherStgStupTask,
        inDefCommonFlagType,
        roleType,
      },
    }));
  };

  const onGetInfraTaskTicketResponse = () => {
    if (!getInfraTaskTicketResponse) return;

    const tcktStsList = createTcktStsTypeList(
      getInfraTaskTicketResponse.tcktTypeTypeCd,
      getInfraTaskTicketResponse.tcktStsTypeCd,
    );

    setPageState((prevState) => ({
      ...prevState,
      searchResult: getInfraTaskTicketResponse,
      tcktStsList,
    }));
  };

  const onListenUpdateTicketResponse = (): void => {
    if (!getUpdateTicketResponse) return;

    if (Array.isArray(getUpdateTicketResponse)) {
      const currentSpreadsheetRecordData: InfraTaskSpreadsheetRecordData[] =
        infraTaskSpreadsheetState
          ? [...infraTaskSpreadsheetState.stupTasksList]
          : [];

      const newSpreadsheetRecordData = mappingErrorResponseToSpreadsheet(
        getUpdateTicketResponse,
        currentSpreadsheetRecordData,
        pageState.displayColumn,
      );

      if (!infraTaskSpreadsheetState) return;

      setInfraTaskSpreadsheetState({
        ...infraTaskSpreadsheetState,
        stupTasksList: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      sendGetInfraTaskTicketRequest({
        tcktId: params.tcktId,
      });
      setPageState((prevState) => ({
        ...prevState,
        isEdited: false,
      }));
    }
  };

  const onInitialDisplayed = () => {
    resetUserError();
    setAppState({ ...appState, title: "Ticket Detail" });
    sendGetInfraTaskTicketRequest({
      tcktId: params.tcktId,
    });
    sendGetGeneralCodesRequest({ cdTypes: requestCdTypes });
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetGeneralCodesResponse, [getGeneralCodesResponse]);
  useEffect(onGetInfraTaskTicketResponse, [getInfraTaskTicketResponse]);
  useEffect(onListenUpdateTicketResponse, [getUpdateTicketResponse]);

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        [name]: value,
      },
      isEdited: true,
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
    }));
  };

  const onChangeDateHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.date;
    const isValid = event.detail.isValid;
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        [name]: value,
      },
      isEdited: true,
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
    }));
  };

  /**
   * Infra Task
   */
  const [
    infraTaskSpreadsheetState,
    setInfraTaskSpreadsheetState,
    initInfraTaskSpreadsheetState,
  ] = useSpreadsheetState<InfraTaskSpreadsheetState>();
  const infraTaskSpreadsheetInitialData = useMemo(() => {
    if (searchResult.stupTasksList === undefined) return;
    const spreadsheetRecordData: InfraTaskSpreadsheetRecordData[] =
      searchResult.stupTasksList.map((e) => {
        return {
          ...ticketDetailPageInitialState.spreadsheet.tmplateRow,
          ...e,
        };
      });

    return {
      stupTasksList: spreadsheetRecordData,
      dataList: dataList,
      necFlgCell: getNecFlgCells(searchResult.stupTasksList),
    };
  }, [searchResult.stupTasksList, dataList]);

  const onCellEditedInfraTaskSpreadsheet = (event: CustomEvent) => {
    if (!infraTaskSpreadsheetState) return;
    const value: string = event.detail.cell.value ?? "";
    const eventName = event.detail.name;

    const stupTasks: StupTasks = event.detail.value.stupTasks;
    const stupTask: StupTask = event.detail.value.stupTask;

    const copyStupTasks = JSON.parse(
      JSON.stringify(infraTaskSpreadsheetState.stupTasksList),
    ) as InfraTaskSpreadsheetRecordData[];

    const targetStupTasks = copyStupTasks.find(
      (e) => e.stupTaskCtgryId === stupTasks.stupTaskCtgryId,
    );

    const targetStupTask = targetStupTasks?.stupTaskList.find(
      (e) => e.stupTaskId === stupTask.stupTaskId,
    );

    const copyNecFlgCells = infraTaskSpreadsheetState?.necFlgCell as NecFlgCell;

    if (!targetStupTasks || !targetStupTask) return;
    if (eventName === "necFlg") {
      targetStupTask.necFlg = value;
      let key =
        targetStupTasks.stupTaskCtgryId + "-" + targetStupTask.stupTaskId;
      copyNecFlgCells[key].isDirty = event.detail.cell.isDirty;
    }
    setInfraTaskSpreadsheetState({
      ...infraTaskSpreadsheetState,
      stupTasksList: copyStupTasks,
      necFlgCell: copyNecFlgCells,
    });
    setPageState((prevState) => ({
      ...prevState,
      isEdited: true,
    }));
  };

  /**
   * Description History
   */

  const [
    _descriptionHistorySpreadsheetState,
    _setDescriptionHistorySpreadsheetState,
    initDescriptionHistorySpreadsheetState,
  ] = useSpreadsheetState<DescriptionHistorySpreadsheetState>();

  const descriptionHistorySpreadsheetInitialData =
    useMemo<DescriptionHistorySpreadsheetState>(() => {
      return {
        tcktDescList: searchResult.tcktDescList,
        removeMilliseconds,
      };
    }, [searchResult.tcktDescList]);

  const onClickEdhProtocolSpreadsheet = (event: CustomEvent) => {
    const rowIndex: number = event.detail.rowIndex;
    const targetRevisionHistory: TcktDesc = event.detail.value;
    if (rowIndex < 1) return;
    setDialogState({ isShow: true, tcktDesc: targetRevisionHistory });
  };

  /**
   * Footer
   */
  const onOkButtonClicked = () => {
    resetUserError();
    if (!infraTaskSpreadsheetState) return;
    const changedStupTasksList: UpdateInfraTaskTicketRequest["stupTasksList"] =
      infraTaskSpreadsheetState.stupTasksList.flatMap((stupTask) =>
        stupTask.stupTaskList.flatMap((e) => {
          const targetKey = `${stupTask.stupTaskCtgryId}-${e.stupTaskId}`;
          if (infraTaskSpreadsheetState.necFlgCell.hasOwnProperty(targetKey))
            return infraTaskSpreadsheetState.necFlgCell[targetKey].isDirty
              ? {
                  stupTaskCtgryId: stupTask.stupTaskCtgryId,
                  stupTaskId: e.stupTaskId,
                  necFlg: e.necFlg,
                  updateDateTime: stupTask.updateDateTime,
                }
              : [];
          return [];
        }),
      );

    const postProcessParams: UpdateInfraTaskTicketRequest = {
      tcktId: searchResult.tcktId,
      stupTasksList: changedStupTasksList,
    };

    const request: UpdateTicketRequest = {
      ...(searchResult.tcktDesc !== getInfraTaskTicketResponse?.tcktDesc && {
        desc: searchResult.tcktDesc,
      }),
      evntGrpTypeCd:
        searchResult.tcktStsTypeCd === getInfraTaskTicketResponse?.tcktStsTypeCd
          ? ""
          : nextInfraEventCode(
              searchResult.tcktTypeTypeCd,
              searchResult.tcktStsTypeCd,
              searchResult.roleCd,
            ),
      tcktId: searchResult.tcktId,
      teamCd: searchResult.teamId,
      dueDate: searchResult.dueDate,
      cmnt: undefined,
      updateDateTime: searchResult.updateDateTime,
      preProcessParams: undefined,
      postProcessParams: postProcessParams,
    };
    sendUpdateTicketRequest(request);
  };

  const onClickSaveButton = () => {
    showConfirmationDialog({
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      type: "confirm",
      onOkButtonClicked,
    });
  };
  const onClickBackButton = () => {
    if (pageState.isEdited) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          navigate(-1);
        },
      });
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      {/* Ticket Info */}
      <>
        <FrVrfAccordion summary="Ticket Entry Form">
          <FrVrfFormPanel>
            <FrVrfSingleLineDisplayField
              label="Ticket Name"
              firstValue={searchResult.tcktName}
            />
            <FrVrfSingleLineDisplayField
              label="Ticket Type"
              firstValue={searchResult.tcktTypeTypeName}
            />
            <FrVrfSingleLineDisplayField
              label="Project"
              firstValue={searchResult.pjtName}
            />
            <FrVrfInputDatepickerField
              labelContent="Due Date"
              defaultDate={searchResult.dueDate}
              onInputValueChanged={onChangeDateHandle("dueDate")}
              errorMessage={errorMessages(fieldPath.dueDate, isModalPage())}
            />
            <FrVrfSingleLineDisplayField
              label="Task Owner Role"
              firstValue={
                pageState.dataList.roleType.find(
                  (e) => e.value === searchResult.roleCd,
                )?.label || ""
              }
            />
            <FrVrfSingleLineDisplayField
              label="Assignee Team"
              firstValue={searchResult.teamId}
            />
            <FrVrfDropdownField
              labelContent="Status"
              selectedValue={searchResult.tcktStsTypeCd}
              onValueChanged={onChangeValueHandle("tcktStsTypeCd")}
              includeDefaultValue={false}
              items={tcktStsList}
              errorMessage={errorMessages(fieldPath.tcktSts, isModalPage())}
            />
            <FrVrfMultipleLineInputField
              style={{
                "--vrf-multiple-line-input-field-input-height": "200px",
                "--vrf-multiple-line-input-field-input-width": "300px",
              }}
              labelContent="Description"
              resize="both"
              content={searchResult.tcktDesc}
              onInputValueChanged={onChangeValueHandle("tcktDesc")}
              errorMessage={errorMessages(fieldPath.desc, isModalPage())}
              constraint="Size(value, {min: 0, max: 2000})"
            />
          </FrVrfFormPanel>
        </FrVrfAccordion>
      </>
      <br />
      {/* Setup Tasks */}
      <FrVrfAccordion summary="Setup Tasks">
        <FrVrfSpreadsheet
          data={infraTaskSpreadsheetInitialData}
          templateUrl={TEMPLATE_FILE_URL.RELATED_TICKET}
          onInitialized={initInfraTaskSpreadsheetState}
          onCellEdited={onCellEditedInfraTaskSpreadsheet}
          maxHeight={720}
        />
      </FrVrfAccordion>
      <br />
      {/* Description History */}
      <FrVrfAccordion summary="Description History">
        <FrVrfSpreadsheet
          data={descriptionHistorySpreadsheetInitialData}
          templateUrl={TEMPLATE_FILE_URL.DESCRIPTION_HISTORY}
          onInitialized={initDescriptionHistorySpreadsheetState}
          onCellClicked={onClickEdhProtocolSpreadsheet}
          maxHeight={720}
        />
      </FrVrfAccordion>
      {/* Footer */}
      <FrVrfFooter>
        <FrVrfButton
          slot="left"
          content="Back"
          theme="secondary"
          size="large"
          onClicked={onClickBackButton}
        />
        <FrVrfButton
          content="Save"
          theme="primary"
          size="large"
          disabled={
            !pageState.isEdited ||
            Object.values(pageState.formValidation).some(
              (isValid) => isValid === false,
            )
          }
          onClicked={onClickSaveButton}
          authActId="ticket_detail.button.save"
        />
      </FrVrfFooter>
    </>
  );
};
